//* Importing from React
import React, { useState, useEffect, useMemo } from "react";

//* Importing necessary modules from React Bootstrap
import {
  Accordion,
  Form,
  Button,
  Spinner,
  Alert,
  Modal,
  Container,
  Row,
  Col,
  Tooltip,
  OverlayTrigger,
} from "react-bootstrap";

//* Importing necessary modules from React Router
import { useNavigate, useParams } from "react-router-dom";

//* Importing custom hooks
import { useFeatures } from "../../hooks/features/useFeature";
import { usePermissions } from "../../hooks/permissions/usePermissions";
import { useUserPermissions } from "../../hooks/permissions/useUserPermissions";

//? Importing types
import type { AddPermissionInput } from "../../hooks/permissions/types/types";

//! Importing common components
import { Loading } from "../../component/common/Loading";
import { getIcon } from "../../component/common/IconMapper";

//* Importing icons
import { FaShieldAlt } from "react-icons/fa";
import { RiArrowGoBackLine } from "react-icons/ri";

//* Importing styles
import "../PermissionView/PermessionFormCollaborators.css";

export const PermessionForm: React.FC = () => {
  const navigate = useNavigate();
  const { id: userIdParam } = useParams<{ id: string }>();

  // Memoize token and userId to prevent unnecessary re-renders
  const token = useMemo(() => localStorage.getItem("token") || "", []);
  const userId = useMemo(() => userIdParam ?? "", [userIdParam]);

  // Fetch features and user permissions using memoized token and userId
  const {
    features,
    loading: featuresLoading,
    error: featuresError,
  } = useFeatures(token);

  const {
    permissions: userPermissions = [],
    loading: permissionsLoading,
    error: permissionsError,
  } = useUserPermissions(token, userId);

  const { addPermissions, isAdding, addError, addSuccess } = usePermissions(
    token,
    userId
  );

  const [permissions, setPermissions] = useState<AddPermissionInput[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

  // Estado para mensagens de sucesso e erro
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const [showErrorMessage, setShowErrorMessage] = useState<string | null>(null);

  // Estado para controlar a inicialização
  const [initialized, setInitialized] = useState<boolean>(false);

  useEffect(() => {
    if (
      !initialized &&
      !featuresLoading &&
      !permissionsLoading &&
      features &&
      userPermissions &&
      features.length > 0
    ) {
      // Mapeia as permissões do usuário e inicializa o estado
      const userPermissionsMap = new Map<string, boolean>();
      userPermissions.forEach((perm) => {
        userPermissionsMap.set(perm.subFeature.id, perm.subFeature.enabled);
      });

      const newPermissions: AddPermissionInput[] = features.flatMap((feature) =>
        feature.subFeatures.map((subFeature) => ({
          id: subFeature.id,
          enabled: userPermissionsMap.get(subFeature.id) || false,
        }))
      );

      setPermissions(newPermissions);
      setInitialized(true);
    }
  }, [
    initialized,
    featuresLoading,
    permissionsLoading,
    features,
    userPermissions,
  ]);

  // useEffect para lidar com a mensagem de sucesso e recarregar a página
  useEffect(() => {
    if (addSuccess) {
      setShowSuccessMessage(true);
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
        // Recarrega a página para atualizar a Sidebar
        window.location.reload();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [addSuccess]);

  // useEffect para lidar com a mensagem de erro
  useEffect(() => {
    if (addError) {
      setShowErrorMessage(addError.message);
      const timer = setTimeout(() => {
        setShowErrorMessage(null);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [addError]);

  const handlePermissionChange = (id: string, enabled: boolean) => {
    setPermissions((prevPermissions) =>
      prevPermissions.map((perm) =>
        perm.id === id ? { ...perm, enabled } : perm
      )
    );
  };

  const handleConfirm = () => {
    setShowConfirmModal(true);
  };

  const handleSubmit = () => {
    const permissionsData = { permissions };
    addPermissions(permissionsData);
    setShowConfirmModal(false);
    // Reset initialized to false to re-fetch permissions after reload
    setInitialized(false);
  };

  if (!userId) {
    return (
      <Container fluid className="mt-5">
        <Alert variant="warning" className="text-center">
          Nenhum usuário selecionado.
        </Alert>
      </Container>
    );
  }

  if (featuresLoading || permissionsLoading) {
    return (
      <Container
        fluid
        className="d-flex justify-content-center align-items-center"
        style={{ height: "60vh" }}
      >
        <Loading />
      </Container>
    );
  }

  if (featuresError) {
    return (
      <Container fluid className="mt-5">
        <Alert variant="danger" className="text-center">
          Erro ao carregar as features: {featuresError}
        </Alert>
      </Container>
    );
  }

  if (permissionsError) {
    return (
      <Container fluid className="mt-5">
        <Alert variant="danger" className="text-center">
          Erro ao carregar as permissões do usuário: {permissionsError}
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4 mb-5 px-3 px-md-5">
      {/* Cabeçalho com Título e Descrição */}
      <div className="header-section mb-4">
        <Row className="align-items-center">
          <Col
            xs={12}
            md={2}
            className="d-flex align-items-center justify-content-center mb-3 mb-md-0"
          >
            <FaShieldAlt size={50} color="#0d6efd" />
          </Col>
          <Col xs={12} md={10}>
            <h1 className="header-title">Gerenciar Permissões do Usuário</h1>
            <p className="header-description text-muted">
              Visualize e ajuste as permissões atribuídas ao usuário
              selecionado. Utilize os interruptores abaixo para habilitar ou
              desabilitar funcionalidades específicas conforme necessário.
            </p>
          </Col>
        </Row>
      </div>

      {/* Formulário de Permissões */}
      <Form onSubmit={(e) => e.preventDefault()}>
        <Accordion defaultActiveKey="0" className="custom-accordion">
          {features
            .sort((a, b) => a.order - b.order)
            .map((feature, index) => (
              <Accordion.Item
                eventKey={index.toString()}
                key={feature.id}
                className="mb-3"
              >
                <Accordion.Header className="accordion-header-custom">
                  <div className="d-flex align-items-center">
                    {getIcon(feature.icon) && (
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{feature.name}</Tooltip>}
                      >
                        <span className="me-3">
                          {React.createElement(getIcon(feature.icon) as any, {
                            size: 25,
                            color: "#0d6efd",
                          })}
                        </span>
                      </OverlayTrigger>
                    )}
                    <span className="fw-bold accordion-title">
                      {feature.name}
                    </span>
                  </div>
                </Accordion.Header>
                <Accordion.Body className="accordion-body-custom">
                  <Row>
                    {feature.subFeatures
                      .sort((a, b) => a.order - b.order)
                      .map((subFeature) => {
                        const perm = permissions.find(
                          (p) => p.id === subFeature.id
                        );
                        return (
                          <Col
                            xs={12}
                            sm={6}
                            md={4}
                            key={subFeature.id}
                            className="mb-4"
                          >
                            <div className="permission-item d-flex align-items-center p-3 border rounded">
                              {getIcon(subFeature.icon) && (
                                <OverlayTrigger
                                  placement="top"
                                  overlay={<Tooltip>{subFeature.name}</Tooltip>}
                                >
                                  <span className="me-3">
                                    {React.createElement(
                                      getIcon(subFeature.icon) as any,
                                      {
                                        size: 30,
                                        color: perm?.enabled
                                          ? "#198754"
                                          : "#6c757d",
                                      }
                                    )}
                                  </span>
                                </OverlayTrigger>
                              )}
                              <Form.Check
                                type="switch"
                                id={`perm-${subFeature.id}`}
                                label={subFeature.name}
                                checked={perm?.enabled || false}
                                onChange={(e) =>
                                  handlePermissionChange(
                                    subFeature.id,
                                    e.target.checked
                                  )
                                }
                                className="flex-grow-1 fs-5"
                              />
                            </div>
                          </Col>
                        );
                      })}
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
            ))}
        </Accordion>

        {/* Botão de Confirmar */}
        <div className="w-100 d-flex justify-content-end mt-4">
          <Button
            variant="secondary"
            onClick={() => navigate(-1)}
            className="d-flex align-items-center px-4 py-2 fs-5 me-2"
          >
            <RiArrowGoBackLine size={24} className="me-2" />
            Voltar
          </Button>
          <Button
            variant="success"
            onClick={handleConfirm}
            disabled={isAdding}
            className="d-flex align-items-center px-4 py-2 fs-5 me-2"
          >
            {isAdding ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Salvando...
              </>
            ) : (
              "Confirmar"
            )}
          </Button>
        </div>

        {/* Feedback de Sucesso e Erro */}
        {showErrorMessage && (
          <Alert variant="danger" className="mt-4">
            <strong>Erro:</strong> {showErrorMessage}
          </Alert>
        )}
        {showSuccessMessage && (
          <Alert variant="success" className="mt-4">
            Permissões atualizadas com sucesso!
          </Alert>
        )}
      </Form>

      {/* Modal de Confirmação */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Alterações</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Tem certeza de que deseja salvar as alterações nas permissões?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmModal(false)}
          >
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};
